import React from 'react';
import { Box, ListSubheader, MenuItem, TextField } from '@mui/material';
import ImageIcon from '@mui/icons-material/Image';
import LanguageIcon from '@mui/icons-material/Language';
import { DEFAULT_MODEL, MODEL_CONFIG } from '../../utils/ModelConfig';

function getModelDetails(modelId) {
  for (const [, models] of Object.entries(MODEL_CONFIG)) {
    if (models[modelId] && typeof models[modelId] === 'object') {
      return {
        logoPath: models.logoPath,
        modelConfig: models[modelId],
      };
    }
  }
  return null;
}

export default function ModelSelectField({
  value,
  onChange,
  includeLegacy = false,
  ...textFieldProps
}) {
  const selectedValue = value || DEFAULT_MODEL;
  const selectedDetails = getModelDetails(selectedValue);
  const selectedValueInMenu = Object.values(MODEL_CONFIG).some((models) => {
    const config = models[selectedValue];
    return (
      config &&
      typeof config === 'object' &&
      (includeLegacy || !config.legacy)
    );
  });

  return (
    <TextField
      select
      value={selectedValue}
      onChange={(event) => onChange?.(event.target.value)}
      SelectProps={{
        renderValue: (currentValue) => {
          const details = getModelDetails(currentValue);
          if (!details) return currentValue;
          const { logoPath, modelConfig } = details;
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
              {logoPath ? (
                <img
                  src={logoPath}
                  alt=""
                  style={{ width: 20, height: 20, marginRight: 8, flexShrink: 0 }}
                />
              ) : null}
              <span style={{ fontSize: '14px', whiteSpace: 'nowrap' }}>
                {modelConfig.label || currentValue}
              </span>
              {(modelConfig.supportsImages || modelConfig.webAccess) && (
                <Box sx={{ display: 'flex', alignItems: 'center', ml: 1, flexShrink: 0 }}>
                  {modelConfig.supportsImages && (
                    <ImageIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  )}
                  {modelConfig.webAccess && (
                    <LanguageIcon sx={{ fontSize: 16, ml: 0.5, color: 'text.secondary' }} />
                  )}
                </Box>
              )}
            </Box>
          );
        },
      }}
      {...textFieldProps}
    >
      {!selectedValueInMenu && (
        <MenuItem value={selectedValue}>
          {selectedDetails?.modelConfig?.label || selectedValue}
        </MenuItem>
      )}
      {Object.entries(MODEL_CONFIG).flatMap(([provider, models]) => {
        const logoPath = models.logoPath;
        const entries = Object.entries(models).filter(
          ([key, config]) =>
            key !== 'logoPath' &&
            typeof config === 'object' &&
            (includeLegacy || !config.legacy)
        );
        if (entries.length === 0) return [];
        return [
          <ListSubheader
            key={provider}
            sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}
          >
            {logoPath ? (
              <img src={logoPath} alt={provider} style={{ width: 20, height: 20 }} />
            ) : null}
            {provider}
          </ListSubheader>,
          ...entries.map(([modelId, config]) => (
            <MenuItem
              key={modelId}
              value={modelId}
              sx={{ display: 'flex', alignItems: 'center', py: 1, px: 2 }}
            >
              {logoPath ? (
                <img
                  src={logoPath}
                  alt={provider}
                  style={{ width: 20, height: 20, marginRight: 8 }}
                />
              ) : null}
              <span style={{ fontSize: '14px' }}>
                {config.label}
                {config.isDefault ? ' (default)' : ''}
              </span>
              <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>
                {config.supportsImages && (
                  <ImageIcon sx={{ fontSize: 16, ml: 0.5, color: 'text.secondary' }} />
                )}
                {config.webAccess && (
                  <LanguageIcon sx={{ fontSize: 16, ml: 0.5, color: 'text.secondary' }} />
                )}
              </Box>
            </MenuItem>
          )),
        ];
      })}
    </TextField>
  );
}
